import React, { useState } from "react";

/* ── Font injected once ── */
const injectFont = () => {
  if (typeof document === "undefined" || document.getElementById("eqc-font")) return;
  const l = document.createElement("link");
  l.id = "eqc-font";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&display=swap";
  document.head.appendChild(l);
};
injectFont();

/* ── Status config ── */
const STATUS = {
  Pending:   { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa", dot: "#f97316" },
  Responded: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe", dot: "#3b82f6" },
  Closed:    { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", dot: "#22c55e" },
};

/* ── Avatar initials + color ── */
const AVATAR_PALETTE = [
  ["#e0e7ff", "#4338ca"], ["#fce7f3", "#be185d"], ["#dcfce7", "#15803d"],
  ["#fef9c3", "#a16207"], ["#f3e8ff", "#7c3aed"], ["#ffe4e6", "#be123c"],
];

const getInitials = (name = "") =>
  name.trim().split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

const getAvatarColor = (name = "") => {
  const idx = name.charCodeAt(0) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[idx];
};

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt)
    ? "—"
    : dt.toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
      });
};

/* ════════════════════════ EnquiryCard ════════════════════════ */
const EnquiryCard = ({ enquiry }) => {
  const [msgExpanded, setMsgExpanded] = useState(false);

  const name      = enquiry.customer_name || enquiry.Username || "Unknown";
  const status    = enquiry.status || "Pending";
  const st        = STATUS[status] || STATUS.Pending;
  const [avBg, avTx] = getAvatarColor(name);
  const msg       = enquiry.Message || "";
  const isLong    = msg.length > 100;
  const shownMsg  = !msgExpanded && isLong ? msg.slice(0, 100) + "…" : msg;

  return (
    <div style={{
      fontFamily: "'Sora', sans-serif",
      background: "#fff",
      border: "1.5px solid #f1f5f9",
      borderRadius: 20,
      overflow: "hidden",
      boxShadow: "0 2px 12px rgba(15,23,42,0.06)",
      transition: "box-shadow 0.2s, transform 0.2s",
      display: "flex",
      flexDirection: "column",
    }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 10px 32px rgba(15,23,42,0.12)";
        e.currentTarget.style.transform = "translateY(-3px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 12px rgba(15,23,42,0.06)";
        e.currentTarget.style.transform = "none";
      }}
    >

      {/* ── Coloured top accent bar ── */}
      <div style={{ height: 4, background: `linear-gradient(90deg, ${avTx}, ${st.dot})` }} />

      <div style={{ padding: "18px 18px 16px" }}>

        {/* ── Header: avatar + name + status ── */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>

          {/* Avatar */}
          <div style={{
            width: 44, height: 44, borderRadius: 14, flexShrink: 0,
            background: avBg, color: avTx,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 700, fontSize: 14, letterSpacing: "0.03em",
          }}>
            {getInitials(name)}
          </div>

          {/* Name + property */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              margin: 0, fontWeight: 600, fontSize: 14.5, color: "#0f172a",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {name}
            </p>
            {enquiry?.ApartmentName  && (
            <p style={{
              margin: "3px 0 0", fontSize: 12, color: "#64748b", fontWeight: 400,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {[enquiry?.ApartmentName].filter(Boolean).join("  ·  ")}
            </p>
            )}
             {enquiry?.project_name  && (
            <p style={{
              margin: "3px 0 0", fontSize: 12, color: "#64748b", fontWeight: 400,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {[enquiry?.project_name].filter(Boolean).join("  ·  ")}
            </p>
            )}
            <p style={{
              margin: "3px 0 0", fontSize: 11.5, color: "#64748b", fontWeight: 400,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {[enquiry.PropertyName, enquiry.PropertyType].filter(Boolean).join("  ·  ")}
            </p>
          </div>

          {/* Status badge */}
          <span style={{
            flexShrink: 0,
            background: st.bg, color: st.color,
            border: `1px solid ${st.border}`,
            borderRadius: 99, padding: "3px 10px",
            fontSize: 10.5, fontWeight: 600,
            display: "inline-flex", alignItems: "center", gap: 5,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: "50%",
              background: st.dot, display: "inline-block",
            }} />
            {status}
          </span>
        </div>

        {/* ── Divider ── */}
        <div style={{ height: 1, background: "#f1f5f9", marginBottom: 13 }} />

        {/* ── Phone number row ── */}
        {enquiry.Mobilenumber && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "#f8fafc", borderRadius: 10, padding: "8px 12px",
            marginBottom: 10,
          }}>
            <span style={{ fontSize: 13 }}>📱</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: "#334155", letterSpacing: "0.04em" }}>
              {enquiry.Mobilenumber}
            </span>
          </div>
        )}

        {/* ── Message bubble ── */}
        {msg && (
          <div style={{
            background: "#f8fafc", borderRadius: 12,
            borderLeft: `3px solid ${avTx}`,
            padding: "10px 13px", marginBottom: 14,
          }}>
            <p style={{ margin: 0, fontSize: 12.5, color: "#475569", lineHeight: 1.65, fontWeight: 400 }}>
              {shownMsg}
            </p>
            {isLong && (
              <button
                onClick={() => setMsgExpanded((p) => !p)}
                style={{
                  marginTop: 5, background: "none", border: "none",
                  padding: 0, cursor: "pointer", fontSize: 11.5,
                  color: avTx, fontFamily: "'Sora', sans-serif", fontWeight: 600,
                }}
              >
                {msgExpanded ? "Show less ↑" : "Read more ↓"}
              </button>
            )}
          </div>
        )}

        {/* ── Action buttons ── */}
        <div style={{ display: "flex", gap: 8, marginBottom: 13 }}>
          {enquiry.Mobilenumber && (
            <a
              href={`tel:${enquiry.Mobilenumber}`}
              rel="noopener noreferrer"
              style={{
                flex: 1, textAlign: "center", textDecoration: "none",
                background: avTx, color: "#fff",
                borderRadius: 11, padding: "9px 0",
                fontSize: 12.5, fontWeight: 600,
                fontFamily: "'Sora', sans-serif",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                transition: "opacity 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              📞 <span>Call</span>
            </a>
          )}
          {enquiry.Email && (
            <a
              href={`mailto:${enquiry.Email}`}
              rel="noopener noreferrer"
              style={{
                flex: 1, textAlign: "center", textDecoration: "none",
                background: "transparent", color: avTx,
                border: `1.5px solid ${avBg === "#e0e7ff" ? "#c7d2fe" : avBg}`,
                borderRadius: 11, padding: "9px 0",
                fontSize: 12.5, fontWeight: 600,
                fontFamily: "'Sora', sans-serif",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = avBg)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              ✉ <span>Email</span>
            </a>
          )}
        </div>

        {/* ── Footer: timestamp ── */}
        <div style={{
          display: "flex", alignItems: "center", gap: 5,
          paddingTop: 10, borderTop: "1px solid #f1f5f9",
        }}>
          <span style={{ fontSize: 11 }}>🕐</span>
          <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>
            {fmtDate(enquiry.created_on)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default EnquiryCard;
